import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Box, Typography, Button } from "@mui/material";
import {
  modalContainerStyles,
  messageContainerStyles,
  messageTextStyles,
  buttonsContainerStyles,
  continueButtonStyles,
  closeButtonStyles,
  toastContainerStyles,
  toastMessageTextStyles,
} from "./styles";

interface NoiseWarningModalProps {
  open: boolean;
  message: string | null;
  onContinue?: () => void;
  onClose?: () => void;
  /** Non-blocking toast that auto-dismisses (roleplay push-to-talk). */
  toast?: boolean;
  autoDismissMs?: number;
}

export const NoiseWarningModal: React.FC<NoiseWarningModalProps> = ({
  open,
  message,
  onContinue,
  onClose,
  toast = false,
  autoDismissMs = 3000,
}) => {
  const onContinueRef = useRef(onContinue);

  useEffect(() => {
    onContinueRef.current = onContinue;
  }, [onContinue]);

  useEffect(() => {
    if (!open || !toast || !message) return;

    const timer = window.setTimeout(() => {
      onContinueRef.current?.();
    }, autoDismissMs);

    return () => window.clearTimeout(timer);
  }, [open, toast, message, autoDismissMs]);

  if (!open || !message) {
    return null;
  }

  if (toast) {
    return createPortal(
      <Box sx={toastContainerStyles}>
        <Typography sx={toastMessageTextStyles}>{message}</Typography>
      </Box>,
      document.body,
    );
  }

  return createPortal(
    <Box sx={modalContainerStyles}>
      <Box sx={messageContainerStyles}>
        {message.split(". ").map((line, index, array) => (
          <Typography
            key={index}
            variant="h6"
            color="error"
            sx={messageTextStyles(index, array.length)}
          >
            {line}
            {index < array.length - 1 ? "." : ""}
          </Typography>
        ))}
      </Box>
      <Box sx={buttonsContainerStyles}>
        <Button
          variant="contained"
          color="primary"
          onClick={onContinue}
          sx={continueButtonStyles}
        >
          Continue
        </Button>
        <Button
          variant="outlined"
          color="error"
          onClick={onClose}
          sx={closeButtonStyles}
        >
          Close
        </Button>
      </Box>
    </Box>,
    document.body,
  );
};
